import { createRouter, createWebHistory } from 'vue-router';

// Lazy load all route components for better code splitting
const HomePage = () => import('@/pages/HomePage.vue');
const DiscoverPage = () => import('@/pages/DiscoverPage.vue');
const StudioPage = () => import('@/pages/studio/StudioPage.vue');
const WidgetPage = () => import('@/pages/studio/WidgetPage.vue');
const CreatorPage = () => import('@/pages/CreatorPage.vue');
const EventsPage = () => import('@/pages/EventsPage.vue');
const TestMarketFromChainPage = () => import('@/pages/test/TestMarketFromChainPage.vue');
const TestHistoryPage = () => import('@/pages/test/TestHistoryPage.vue');
const TestCreatorMarketsPage = () => import('@/pages/test/TestCreatorMarketsPage.vue');
const StudioGuidePage = () => import('@/pages/studio/StudioGuidePage.vue');
const UserGuidePage = () => import('@/pages/UserGuidePage.vue');
const TestNotificationsPage = () => import('@/pages/test/TestNotificationsPage.vue');
const TestAnimationLib = () => import('@/pages/test/TestAnimationLib.vue');
const TestTopUpModal = () => import('@/pages/test/TestTopUpModal.vue');
const TestPosthog = () => import('@/pages/test/TestPosthog.vue');
const TestLandingPage = () => import('@/pages/landingPages/allIn/ContentAllIn.vue');
const PageAllIn = () => import('@/pages/landingPages/allIn/PageAllIn.vue');

export enum RouteGroup {
  Predictions = 'predictions',
  Studio = 'studio',
  Test = 'test',
  LandingPages = 'landing-pages',
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/events',
      name: 'EventsPage',
      component: EventsPage,
    },
    {
      path: '/',
      meta: { group: RouteGroup.Predictions },
      children: [
        {
          path: '/',
          name: 'HomePage',
          component: HomePage,
        },
        {
          path: '/discover',
          name: 'DiscoverPage',
          component: DiscoverPage,
        },
        {
          path: '/official',
          name: 'OfficialCreatorPage',
          component: CreatorPage,
          props: { creatorId: '1' },
        },
        {
          path: '/:creatorId(\\d+)',
          name: 'CreatorPage',
          component: CreatorPage,
          props: true,
        },
        {
          path: '/guide',
          name: 'UserGuidePage',
          component: UserGuidePage,
        },
      ],
    },
    {
      path: '/',
      meta: { group: RouteGroup.LandingPages },
      children: [
        {
          path: 'all-in',
          name: 'AllInLandingPage',
          component: PageAllIn,
        },
      ],
    },
    {
      path: '/studio',
      meta: { group: RouteGroup.Studio },
      children: [
        {
          path: '',
          name: 'StudioPage',
          component: StudioPage,
        },
        {
          path: 'guide',
          name: 'StudioGuidePage',
          component: StudioGuidePage,
        },
        {
          path: 'widget/:creatorId(\\d+)',
          name: 'WidgetPage',
          component: WidgetPage,
        },
      ],
    },
    {
      path: '/test',
      meta: { group: RouteGroup.Test },
      children: [
        {
          path: 'market-from-chain',
          name: 'TestMarketFromChainPage',
          component: TestMarketFromChainPage,
        },
        {
          path: 'history',
          name: 'TestHistoryPage',
          component: TestHistoryPage,
        },
        {
          path: 'creator-markets',
          name: 'TestCreatorMarketsPage',
          component: TestCreatorMarketsPage,
        },
        {
          path: 'notifications',
          name: 'TestNotificationsPage',
          component: TestNotificationsPage,
        },
        {
          path: 'animation-lib',
          name: 'TestAnimationLibPage',
          component: TestAnimationLib,
        },
        {
          path: 'top-up-modal',
          name: 'TestTopUpModal',
          component: TestTopUpModal,
        },
        {
          path: 'posthog',
          name: 'TestPostHogPage',
          component: TestPosthog,
        },
        {
          path: 'landing-page',
          name: 'TestLandingPage',
          component: TestLandingPage,
        },
      ],
    },
  ],
});

// We need to scroll to top on route change
router.afterEach(() => {
  window.scrollTo(0, 0);
});

// Defer PostHog initialization to avoid forced reflows during initial page load
// PostHog's recorder causes 37ms of forced reflows that block rendering
// Initialize after browser is idle to eliminate performance impact
const initPostHog = () => {
  import('@/composables/usePostHog').then(({ default: usePostHog }) => {
    usePostHog();
  });
};

if (import.meta.env.PROD) {
  // Production: Defer to avoid blocking initial render
  if ('requestIdleCallback' in window) {
    requestIdleCallback(initPostHog, { timeout: 2000 });
  } else {
    setTimeout(initPostHog, 1000);
  }
} else {
  // Development: Load immediately for debugging
  initPostHog();
}

export default router;
